import { Button, Input } from 'antd';
import { ColumnsType, ColumnType } from 'antd/es/table';
import { SortOrder } from 'antd/es/table/interface';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { HiSortAscending, HiSortDescending } from 'react-icons/hi';
import { StyledHeaderBar, StyledTable, TableWrapper } from './style';
import { TableProps } from './types';
import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/16/solid';
import { TableProps as AntdTableProps } from 'antd/es/table';
import { SearchNormal1 } from 'iconsax-react';

function withDefaultSortIcon<T>(cols: ColumnsType<T>): ColumnsType<T> {
  return cols.map((col) => {
    if (!col.sorter) return col;

    return {
      ...col,
      sortIcon: ({ sortOrder }: { sortOrder: SortOrder }) =>
        sortOrder === 'ascend' ? (
          <HiSortAscending
            size={18}
            className={`${sortOrder === 'ascend' ? 'text-primaryBlue' : 'text-primaryBlack'} mt-1`}
          />
        ) : (
          <HiSortDescending
            size={18}
            className={`${sortOrder === 'descend' ? 'text-primaryBlue' : 'text-primaryBlack'} mt-1`}
          />
        ),
    } as ColumnType<T>;
  });
}

export function Table({
  title,
  dataSource = [],
  columns,
  onSearch,
  searchPlaceholder = 'Search…',
  selectable,
  selectedRowKeys,
  onSelect,
  showPagination = true,
  paginationProps = {},
  onPageChange,
  loading,
  onChange,
  customEmpty,
  customEmptyText = 'No data',
  footer,
  showSummary = true,
  summaryFormat = 'Showing {{start}}-{{end}} of {{total}} entries',
  rowKey,
  rowClassName,
  className,
  headerClassName,
  searchValue,
  onRowClick,
  ...props
}: TableProps) {
  const [value, setValue] = useState('');

  const pageSize = paginationProps?.pageSize ?? 10;
  const defaultPage = paginationProps?.defaultCurrent ?? 1;
  const [currentPage, setCurrentPage] = useState<number>(defaultPage);

  const enhancedColumns = withDefaultSortIcon(columns || []);

  useEffect(() => {
    if (paginationProps.current) {
      setCurrentPage(paginationProps.current);
    }
  }, [paginationProps.current]);

  const total = paginationProps.total ?? dataSource.length;
  const start = total > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const end = Math.min(currentPage * pageSize, total);
  const totalPages = Math.ceil(total / pageSize);

  const summaryText = summaryFormat
    .replace('{{start}}', start.toString())
    .replace('{{end}}', end.toString())
    .replace('{{total}}', total?.toString());

  const handlePageChange = (page: number, size: number) => {
    setCurrentPage(page);
    onPageChange?.(page, size);
    onSelect?.([]);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    onSearch?.(e);
    onSelect?.([]);
  };

  const getKey = (record: string): React.Key =>
    typeof rowKey === 'function'
      ? rowKey(record)
      : (record as any)[rowKey ?? 'key'];

  const handleSelectChange = (record: any, selected: boolean) => {
    const key = getKey(record);
    const newKeys = selected
      ? [...(selectedRowKeys ?? []), key]
      : (selectedRowKeys ?? []).filter((k) => k !== key);

    onSelect?.(newKeys);
  };

  const handleSelectAll = (selected: boolean, allRows: any[]) => {
    const newKeys = selected ? allRows.map(getKey) : [];
    onSelect?.(newKeys);
  };

  const rowSelection = selectable
    ? {
        selectedRowKeys: selectedRowKeys ?? [],
        onSelect: handleSelectChange,
        onSelectAll: handleSelectAll,
      }
    : undefined;

  const renderPageNumbers = () => {
    const maxPagesToShow = 5;
    const pages: (number | '...')[] = [];

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      const startPage = Math.max(2, currentPage - 1);
      const endPage = Math.min(totalPages - 1, currentPage + 1);

      pages.push(1);

      if (startPage > 2) pages.push('...');

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      if (endPage < totalPages - 1) pages.push('...');

      pages.push(totalPages);
    }

    return pages.map((p, index) =>
      p === '...' ? (
        <span key={`ellipsis-${index}`} className='px-2'>
          ...
        </span>
      ) : (
        <Button
          key={p}
          className={`w-6 h-6 p-1 ${
            currentPage === p
              ? 'border-wl-primary text-wl-primary hover:!border-wl-primary hover:!text-wl-primary'
              : 'hover:!border-wl-primary hover:!text-wl-primary'
          }`}
          onClick={() => handlePageChange(p, pageSize)}
        >
          {p}
        </Button>
      )
    );
  };

  return (
    <TableWrapper className={clsx(className, 'bg-wl-background rounded-2xl')}>
      {(title || onSearch) && (
        <StyledHeaderBar
          className={clsx(
            'flex flex-col md:flex-row gap-2 w-full',
            headerClassName
          )}
        >
          <div className='flex flex-col md:flex-row items-center space-x-2 w-full'>
            {title && (
              <span className='text-M-20 text-wl-on-background'>{title}</span>
            )}
          </div>
          <div className='flex flex-col md:flex-row items-center gap-2 justify-end w-full'>
            {onSearch && (
              <>
                <Input
                  size='large'
                  suffix={!value && <SearchNormal1 size={16} />}
                  variant='outlined'
                  className='w-full h-10 sm:w-[360px] !pr-3'
                  placeholder={searchPlaceholder}
                  allowClear
                  onChange={handleChange}
                  value={searchValue}
                  style={{
                    boxShadow: 'none',
                    outline: 'none',
                  }}
                />
              </>
            )}
          </div>
        </StyledHeaderBar>
      )}

      <StyledTable
        {...props}
        className='px-6 pt-6'
        columns={enhancedColumns}
        dataSource={dataSource}
        scroll={{ x: 'max-content' }}
        loading={{
          spinning: typeof loading === 'boolean' ? loading : !!loading,
          className: 'h-full !max-h-[unset]',
          indicator: (
            <AiOutlineLoading3Quarters size={24} className='animate-spin' />
          ),
        }}
        pagination={false}
        rowKey={rowKey}
        rowClassName={(record, index) =>
          clsx(
            typeof rowClassName === 'function'
              ? rowClassName(record, index)
              : rowClassName,
            onRowClick && 'cursor-pointer'
          )
        }
        locale={{ emptyText: customEmpty ?? customEmptyText }}
        onChange={onChange}
        rowSelection={rowSelection}
        onRow={(record, index) => {
          const parentOnRow =
            (props as AntdTableProps<typeof record>).onRow?.(record, index) ||
            {};

          const ourOnClick = () => onRowClick?.(record, index);

          return {
            ...parentOnRow,
            onClick: parentOnRow.onClick
              ? (event) => {
                  parentOnRow.onClick?.(event);
                  ourOnClick();
                }
              : ourOnClick,
          };
        }}
      />

      {(showSummary || showPagination) && totalPages > 1 && (
        <div className='text-textPrimary flex justify-between flex-col md:flex-row items-center mt-4 text-sm pb-6 px-6'>
          {showSummary && (
            <div className='text-wl-on-background'>{summaryText}</div>
          )}
          {showPagination && (
            <div className='flex items-center gap-2'>
              <div className='flex space-x-1'>
                <Button
                  className='w-6 h-6 p-1 bg-wl-background'
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(1, pageSize)}
                >
                  <ChevronDoubleLeftIcon
                    className={`w-4 h-4  ${currentPage === 1 ? 'text-wl-disabled' : 'text-wl-on-background'}`}
                  />
                </Button>

                <Button
                  type='text'
                  className='w-6 h-6 p-1'
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1, pageSize)}
                >
                  <ChevronLeftIcon
                    className={`w-4 h-4  ${currentPage === 1 ? 'text-wl-disabled' : 'text-wl-on-background'}`}
                  />
                </Button>

                {renderPageNumbers()}
                <Button
                  type='text'
                  className='w-6 h-6 p-1'
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1, pageSize)}
                >
                  <ChevronRightIcon
                    className={`w-4 h-4  ${currentPage === totalPages ? 'text-wl-disabled' : 'text-wl-on-background'}`}
                  />
                </Button>

                <Button
                  className='w-6 h-6 p-1 bg-wl-background'
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(totalPages, pageSize)}
                >
                  <ChevronDoubleRightIcon
                    className={`w-4 h-4  ${currentPage === totalPages ? 'text-wl-disabled' : 'text-wl-on-background'}`}
                  />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
      {footer}
    </TableWrapper>
  );
}
